import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as commonmark from 'commonmark';
import { useReconnectingWebSocket } from '../hooks/useReconnectingWebSocket';
import { sanitizeMarkdownHtml } from '../lib/sanitize';

interface Message {
  text: string;
  isUser: boolean;
  isComplete?: boolean;
}

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [trialError, setTrialError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const parser = useRef(new commonmark.Parser());
  const renderer = useRef(new commonmark.HtmlRenderer({ safe: true }));

  const handleWsMessage = useCallback((event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data);
      if (message.error) {
        setTrialError(message.error);
        return;
      }
      if (message.type === 'text' && message.data) {
        setMessages((prev) => {
          const newMessages = [...prev];
          if (
            newMessages.length > 0 &&
            !newMessages[newMessages.length - 1].isUser &&
            !newMessages[newMessages.length - 1].isComplete
          ) {
            const lastMessage = newMessages[newMessages.length - 1];
            if (!lastMessage.text.endsWith(message.data)) {
              lastMessage.text += message.data;
            }
          } else {
            newMessages.push({ text: message.data, isUser: false, isComplete: false });
          }
          return newMessages;
        });
      } else if (message.type === 'end') {
        setMessages((prev) => {
          const newMessages = [...prev];
          if (newMessages.length > 0 && !newMessages[newMessages.length - 1].isUser) {
            newMessages[newMessages.length - 1].isComplete = true;
          }
          return newMessages;
        });
      }
    } catch (error) {
      console.error('Error processing message:', error);
    }
  }, []);

  const { send, isConnected, isServerReady, isReconnecting, connectionError } =
    useReconnectingWebSocket({
      path: '/chat',
      onMessage: handleWsMessage,
    });

  const renderMarkdown = (text: string): string => {
    const parsed = parser.current.parse(text);
    return sanitizeMarkdownHtml(renderer.current.render(parsed));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputMessage]);

  const canSend = isConnected && isServerReady && !isReconnecting;

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || !canSend) return;

    send(
      JSON.stringify({
        type: 'text',
        data: inputMessage,
      }),
    );

    setMessages((prev) => [...prev, { text: inputMessage, isUser: true, isComplete: true }]);
    setInputMessage('');
  };

  const connectionLabel = isReconnecting
    ? 'Reconectando…'
    : isConnected && !isServerReady
      ? 'Conectando al asistente…'
      : isConnected
        ? 'Conectado'
        : 'Desconectado';

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md overflow-hidden relative">
          {trialError && (
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
              role="dialog"
              aria-modal="true"
              aria-labelledby="chat-trial-error-title"
            >
              <div className="bg-white p-8 rounded-lg shadow-lg max-w-md mx-4 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 id="chat-trial-error-title" className="text-xl font-bold text-gray-900 mb-2">Función No Disponible</h3>
                <p className="text-gray-600 mb-6">
                  Esta función no está disponible. Por favor, contacta al administrador del
                  calendario para activar esta función.
                </p>
              </div>
            </div>
          )}

          <header className="bg-primary text-white p-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div
                className={`w-3 h-3 ${canSend ? 'bg-green-400' : 'bg-yellow-400'} rounded-full`}
                aria-hidden="true"
              ></div>
              <h1 className="text-xl font-bold">Asistente Fácil Chat</h1>
              <span className="text-sm opacity-90" aria-hidden="true">
                ({connectionLabel})
              </span>
              <span className="sr-only">{connectionLabel}</span>
            </div>
          </header>

          <div
            className="flex-1 overflow-y-auto p-6 space-y-4 h-[calc(100vh-16rem)]"
            aria-live="polite"
            aria-relevant="additions"
          >
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl p-4 ${
                    message.isUser ? 'bg-secondary text-white' : 'bg-gray-100'
                  } markdown-content`}
                >
                  {message.isUser ? (
                    message.text
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(message.text) }}
                      className="prose prose-sm max-w-none dark:prose-invert prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-pre:my-1 prose-blockquote:my-1"
                    />
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
            {connectionError && (
              <div className="text-center text-red-600 bg-red-50 p-4 rounded-lg border border-red-200">
                {connectionError}
              </div>
            )}
          </div>

          <form onSubmit={handleSendMessage} className="p-4 bg-white border-t border-gray-100">
            <div className="flex space-x-4">
              <textarea
                ref={textareaRef}
                rows={1}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Escribe tu mensaje... (Enter para enviar, Shift+Enter para nueva línea)"
                aria-label="Mensaje de chat"
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none min-h-[48px] max-h-[200px] scrollbar-hide"
                disabled={!canSend}
              />
              <button
                type="submit"
                className="bg-secondary text-white px-6 py-3 rounded-lg hover:bg-secondary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 disabled:bg-gray-400 h-[48px] self-end"
                disabled={!canSend}
              >
                Enviar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Chat;
